<template>
  <v-wait for="assets">
    <div slot="waiting">
      <div class="mt-4 text-lg text-grey-700 font-medium text-center w-full">
        {{ $t('frontend_resource_promo_connecting_to_groovesell', {appTitle: whitelabelAppData('sell').title}) }}
      </div>
    </div>

    <affiliate-portal-shell>
      <template slot="header">
        <promo-tools-header
          :assets-tab="assetsTab"
          :funnel-resource="funnelResource"
          :related-funnels="relatedFunnels"
          :other-resources="otherResources"
          :selected-promo-link="selectedPromoLink"
          :is-registered="isRegistered"
          :direct-affiliate-links="directAffiliateLinks"
          :assets-tabs="assetsTabs"
          :user-name="userName"
          :show-elements="showElements"
          @goBackToPromoToolsNavigation="goBackToPromoToolsNavigation"
          @changedPromoTool="changedPromoTool"
          @onCopy="onCopy"
          @onError="onError"
          @openPromotoolsSection="openPromotoolsSection"
          @logoutAffiliate="logoutAffiliate"
          @onToggleSwitchValue="onToggleSwitchValue"
          @changedFunnel="changedFunnel"
        />
      </template>

      <template slot="hero">
        <link-hero
          v-if="isRegistered && affiliateLink"
          :link="affiliateLink"
          :links="affiliateLinks"
          :selected-id="selectedAffiliateLinkId"
          @select="selectAffiliateLink"
          @copy="onCopy"
          @error="onError"
          @open="openAffiliateLink"
        />
      </template>

      <template slot="rail">
        <section-rail
          v-if="isRegistered && sectionItems.length"
          :items="sectionItems"
          :current="assetsTab"
          @select="openPromotoolsSection"
        />
      </template>

      <transition name="fade">
        <div
          v-if="isRegistered"
          class="w-full gs-portal-content"
          :class="{ 'gs-portal-content--scroll': !showElements }"
        >
          <main-tab
            v-if="assetsTab === ''"
            :assets-tabs="assetsTabs"
            :funnel-resource="funnelResource"
            :show-elements="showElements"
            @openPromotionalResourcesPage="openPromotionalResourcesPage"
            @openPromotoolsSection="openPromotoolsSection"
          />
          <promotools-tabs
            @select="openPromotoolsSection"
            v-else
            :assets-tab="assetsTab"
            :funnel-resource="funnelResource"
            :direct-affiliate-links="directAffiliateLinks"
          />
        </div>
        <default-tab
          v-else
          :funnel-resource="funnelResource"
          :selected-promo-link="selectedPromoLink"
          @changedFunnel="changedFunnel"
          @openPromotionalResourcesPage="openPromotionalResourcesPage"
        />
      </transition>
    </affiliate-portal-shell>
  </v-wait>
</template>

<script>
import axios from 'axios';
import 'js-video-url-parser/lib/provider/youtube';
import 'js-video-url-parser/lib/provider/vimeo';
import Notify from '@groovepages/gd-ui-kit/notify';
import auth from '@groovepages/gd-lib/auth';
import { getEmbeddedUser } from '@/services/portal-embed-session';
import FunnelResource from '@/models/FunnelResource';
import PromoToolsHeader from './partials/PromoToolsHeader.vue';
import MainTab from './partials/tabs/MainTab.vue';
import PromotoolsTabs from './partials/PromotoolsTabs.vue';
import DefaultTab from './partials/tabs/DefaultTab.vue';
import AffiliatePortalShell from './AffiliatePortalShell.vue';
import LinkHero from './LinkHero.vue';
import SectionRail from './SectionRail.vue';
import { portalSections, affiliateLinkId } from './portalSections';

export default {
  components: {
    PromoToolsHeader,
    MainTab,
    PromotoolsTabs,
    DefaultTab,
    AffiliatePortalShell,
    LinkHero,
    SectionRail,
  },
  props: {
    showElements: {
      type: Boolean,
      default: true,
    },
    permalink: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      resourceId: this.permalink || this.$route.params.resource_id,
      isRegistered: 1,
      relatedFunnels: [],
      otherResources: [],
      userName: '',
      funnel: null,
      funnelResource: null,
      assetsTabs: [],
      assetsTab: '',
      selectedPromoLink: {
        funnel: '',
        permalink: '',
      },
      selectedAffiliateLinkId: null,
    };
  },
  computed: {
    directAffiliateLinks() {
      if (this.funnelResource && this.funnelResource.display_links) {
        return this.funnelResource.display_links.filter((link) => {
          return link.type == 'affiliate';
        });
      }
    },
    sectionItems() {
      return portalSections(this.assetsTabs, this.funnelResource);
    },
    affiliateLinks() {
      return this.directAffiliateLinks || [];
    },
    selectedAffiliateLink() {
      if (this.selectedAffiliateLinkId === null) {
        return null;
      }

      return this.affiliateLinks.find(
        (link, index) => affiliateLinkId(link, index) === this.selectedAffiliateLinkId
      ) || null;
    },
    affiliateLink() {
      if (this.selectedAffiliateLink) {
        return this.selectedAffiliateLink.link;
      }

      if (this.funnelResource && this.funnelResource.home_page_link) {
        return this.funnelResource.home_page_link;
      }

      return this.affiliateLinks.length ? this.affiliateLinks[0].link : '';
    },
  },
  async created() {
    this.$wait.start('assets');
    const loggedInUser = getEmbeddedUser() || auth.authenticatedUser();
    this.userName = loggedInUser.name;
    await this.fetchFunnelResource(this.resourceId);
    if (
      this.funnelResource &&
      this.funnelResource.show_more_funnels &&
      this.funnelResource.related_funnels.length &&
      this.funnelResource.related_funnels.length > 0
    ) {
      this.relatedFunnels = this.funnelResource.related_funnels;
    } else {
      this.relatedFunnels = [];
    }

    if (
      this.funnelResource &&
      this.funnelResource.remaining_resources &&
      this.funnelResource.remaining_resources.length &&
      this.funnelResource.remaining_resources.length > 0
    ) {
      this.otherResources = this.funnelResource.remaining_resources;
    } else {
      this.otherResources = [];
    }

    this.$wait.end('assets');
  },
  methods: {
    onToggleSwitchValue(data) {
      this.assetsTabs = data.assetsTabs;
      this.funnelResource = data.funnelResource;
    },
    openPromotionalResourcesPage() {
      const pagesAppTitle = this.whitelabelAppData('pages').title.toLowerCase();
      window.open(
        `https://grvcm.${pagesAppTitle}.com/gpiframes/page-28`,
        '_blank'
      );
    },
    logoutAffiliate() {
      auth.logout();
      this.$emit('logoutAffiliate');
    },
    goBackToPromoToolsNavigation() {
      this.assetsTab = '';
    },
    openPromotoolsSection(sectionName) {
      this.assetsTab = sectionName;
    },
    selectAffiliateLink(id) {
      this.selectedAffiliateLinkId = id;
    },
    openAffiliateLink() {
      if (!this.affiliateLink) {
        return;
      }

      const opened = window.open(this.affiliateLink, '_blank');

      if (opened) {
        opened.opener = null;
      }
    },
    async changedFunnel(selectedPromoLinkFunnel = null) {
      const selectedFunnelId = selectedPromoLinkFunnel || this.selectedPromoLink.funnel;
      const selectedfunnelResourcePermalink = await FunnelResource.params({
        funnel: selectedFunnelId,
      })
        .custom('fetch-default-funnel-resource')
        .$first();

      this.resourceId = selectedfunnelResourcePermalink.funnel_resource.permalink;
      this.otherResources = selectedfunnelResourcePermalink.funnel_resource.remaining_resources;

      this.selectedPromoLink.permalink = this.resourceId;
      this.fetchFunnelResource(this.selectedPromoLink.permalink);
    },
    async fetchFunnelResource(resourceId) {
      const funnelResourceData = await axios.get(`fetch-funnel-resource`, {
        params: {
          permalink: resourceId,
        },
      });
      if (funnelResourceData.data.status != 'success') {
        this.isRegistered = 0;
      } else {
        this.funnelResource = funnelResourceData.data.funnel_resource;
        this.selectedPromoLink.permalink = resourceId;

        this.selectedPromoLink.funnel = this.funnelResource.funnel_id;
        this.assetsTabs = [];
        this.assetsTabs.push({
          title: 'Custom Landing Page Links',
          value: 'links',
          completed: 0,
          id: 0,
        });
        this.assetsTabs.push({ title: 'Affiliate Recruitment Links', value: 'recruitmentLinks', completed: 0, id: -2 });

        this.assetsTabs.push({
          title: 'Stats',
          value: 'stats',
          completed: 0,
          id: -1,
        });

        this.assetsTabs.push({
          title: 'Swipes',
          value: 'swipes',
          completed: this.funnelResource.affiliate_resource.swipes_completed,
          id: 1,
        });

        this.assetsTabs.push({
          title: `Blog Reviews`,
          value: 'blogReviews',
          completed:
            this.funnelResource.affiliate_resource.blog_reviews_completed,
          id: 2,
        });

        this.assetsTabs.push({
          title: `Articles`,
          value: 'allArticles',
          completed:
            this.funnelResource.affiliate_resource.press_releases_completed,
          id: 8,
        });

        this.assetsTabs.push({
          title: `Banners`,
          value: 'banners',
          completed: this.funnelResource.affiliate_resource.banners_completed,
          id: 3,
        });

        this.assetsTabs.push({
          title: `Thank You Page Ads`,
          value: 'thankyouPageAds',
          completed:
            this.funnelResource.affiliate_resource.thankyou_page_ads_completed,
          id: 4,
        });

        this.assetsTabs.push({
          title: `Logout Page Ads`,
          value: 'logoutPageAds',
          completed:
            this.funnelResource.affiliate_resource.logout_page_ads_completed,
          id: 5,
        });

        this.assetsTabs.push({
          title: `Social Media`,
          value: 'socialMediaPosts',
          completed:
            this.funnelResource.affiliate_resource.social_media_posts_completed,
          id: 7,
        });

        this.assetsTabs.push({
          title: `Video Reviews`,
          value: 'videoReviews',
          completed:
            this.funnelResource.affiliate_resource.video_reviews_completed,
          id: 9,
        });

        this.assetsTabs.push({
          title: `Facebook Ads`,
          value: 'facebookAds',
          completed:
            this.funnelResource.affiliate_resource.facebook_ads_completed,
          id: 6,
        });

        this.assetsTabs.push({
          title: `Signatures`,
          value: 'signatures',
          completed:
            this.funnelResource.affiliate_resource.signatures_completed,
          id: 10,
        });

        this.isRegistered = 1;
      }
    },
    changedPromoTool(selectedPromoLinkPermalink) {
      this.resourceId = selectedPromoLinkPermalink;
      this.fetchFunnelResource(selectedPromoLinkPermalink);
    },
    onCopy() {
      Notify.success(this.$t('successfully_copied'));
    },
    onError() {
      Notify.fail(this.$t('frontend_unable_copy_to_clipboard'));
    },
  },
  watch: {
    permalink() {
      this.resourceId = this.permalink;
    },
    funnelResource() {
      this.selectedAffiliateLinkId = null;
    },
  },
};
</script>

<style scoped>
.gs-portal-content {
  min-width: 0;
}

/* Embedded in the marketplace `gd-modal` (showElements=false) the widget has no
   page to scroll, so the section content keeps its own scroll container. The
   modal is opened with :allow-overflow="true" so the panel scrolls too. No
   viewport units: the hosted page is iframe-resized from natural height. */
.gs-portal-content--scroll {
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
